import { useSyncExternalStore } from 'react';
import type { NavigationAdapter } from '@lumen/platform';
import { pageIdFromSegment } from '@lumen/data';

export type Route =
  | { name: 'login' }
  | { name: 'signup'; email: string | null }
  | { name: 'forgot' }
  | { name: 'reset'; token: string }
  | { name: 'verify'; token: string }
  | { name: 'join'; token: string }
  | { name: 'root' }
  | { name: 'workspace'; ws: string }
  | { name: 'home'; ws: string }
  | { name: 'universe'; ws: string; focus: string | null }
  | { name: 'page'; ws: string; pageId: string; blockId: string | null }
  | { name: 'site'; ws: string; pageId: string }
  | { name: 'notfound'; ws: string | null; segment: string };

export function parseRoute(path: string, hash: string, query = ''): Route {
  const parts = path.split('/').filter(Boolean).map(decodeURIComponent);
  const params = new URLSearchParams(query);
  if (parts.length === 0) return { name: 'root' };
  if (parts[0] === 'login') return { name: 'login' };
  if (parts[0] === 'signup') return { name: 'signup', email: params.get('email') };
  if (parts[0] === 'forgot') return { name: 'forgot' };
  if (parts[0] === 'reset' && parts.length === 1) return { name: 'reset', token: params.get('token') ?? '' };
  if (parts[0] === 'verify' && parts.length === 1) return { name: 'verify', token: params.get('token') ?? '' };
  if (parts[0] === 'join' && parts[1] && parts.length === 2) return { name: 'join', token: parts[1] };
  if (parts[0] === 'site' && parts[1] && parts[2]) {
    const id = pageIdFromSegment(parts[2]);
    return id ? { name: 'site', ws: parts[1], pageId: id } : { name: 'notfound', ws: parts[1], segment: parts[2] };
  }
  const ws = parts[0]!;
  if (parts.length === 1) return { name: 'workspace', ws };
  if (parts[1] === 'home') return { name: 'home', ws };
  if (parts[1] === 'universe' && parts.length === 2) return { name: 'universe', ws, focus: pageIdFromSegment(params.get('page') ?? '') };
  const id = pageIdFromSegment(parts[1]!);
  if (!id) return { name: 'notfound', ws, segment: parts[1]! };
  return { name: 'page', ws, pageId: id, blockId: /^[0-9a-f-]{8,}$/i.test(hash) ? hash : null };
}

export function useRoute(nav: NavigationAdapter): Route {
  const key = useSyncExternalStore(
    (l) => nav.subscribe(l),
    () => {
      const c = nav.current();
      return `${c.path}\n${c.hash}\n${c.query}`;
    },
  );
  const [path, hash = '', query = ''] = key.split('\n');
  return parseRoute(path!, hash, query);
}
